import React, { useEffect, useState } from 'react';
import classNames from 'classnames';

import { useCodeBlockEditorContext } from '@mdxeditor/editor';

import { handleCopy } from '../../../../helpers/handleCopy';
import { setSyncTimeout } from '../../../../helpers/setSyncTimeout';
import { isDiagramSyntaxValid } from '../../../../helpers/diagramsUtils';

import { useOverlayState } from '../../../../hooks/useOverlayState';
import { useInitCodePlugin } from './hooks/useInitCodePlugin';

import { COPY_EXCLUDED_ELEMENT_CLASS_NAME } from '../../../../helpers/handleCopyFormattedText';

import { SvgIcon } from '../../../index';
import EyeIcon from '../../../Icons/EyeIcon';
import CopyIcon from '../../../Icons/CopyIcon';
import PreviewMode from './PreviewMode/PreviewMode';
import CheckLineIcon from '../../../Icons/CheckLineIcon';
import CodeLanguageSelect, { EMPTY_LANGUAGE_VALUE } from './CodeLanguageSelect/CodeLanguageSelect';

export const LANGUAGES_WITH_PREVIEW = ['plotly', 'mermaid'];
export const CODE_BLOCK_CLASS_NAME = 'code-block';
export const CODE_BLOCK_PREVIEW_MODE_CLASS_NAME = 'code-block-preview-mode';

export const CodeMirrorEditor = ({ language, nodeKey, code, focusEmitter }) => {
    const { parentEditor, lexicalNode } = useCodeBlockEditorContext();

    const { elRef, readOnly } = useInitCodePlugin({ nodeKey, language, code, focusEmitter });

    const [copied, setCopied] = useState(false);

    const [mode, setMode] = useState(() => {
        if (!LANGUAGES_WITH_PREVIEW.includes(language)) return 'edit';
        if (!code) return 'edit';
        if (language === 'plotly') {
            try {
                JSON.parse(code);
                return 'preview';
            } catch (e) {
                return 'edit';
            }
        }
        if (language === 'mermaid') {
            return null;
        }
        return 'preview';
    });

    useEffect(() => {
        const checkMermaidSyntax = async () => {
            const isValid = await isDiagramSyntaxValid(code);
            setMode(isValid ? 'preview' : 'edit');
        };

        if (!mode) {
            checkMermaidSyntax();
        }
    }, [mode]);

    const { isOpened: isLangSelectOpened, toggle } = useOverlayState();

    const onLanguageChange = (language) => {
        parentEditor.update(() => {
            lexicalNode.setLanguage(language === EMPTY_LANGUAGE_VALUE ? '' : language);
            setTimeout(() => {
                parentEditor.update(() => {
                    lexicalNode.getLatest().select();
                });
            });
        });
    };

    useEffect(() => {
        let stopTimeout;
        if (copied) {
            stopTimeout = setSyncTimeout(() => setCopied(false), 2000);
        }

        return () => {
            if (stopTimeout) {
                stopTimeout();
            }
        };
    }, [copied]);

    const buttonGenericStyles =
        'w-[28px] h-[28px] flex items-center justify-center bg-[#d0d7de] rounded-[5px] p-1.5 hover:bg-[#116329] hover:text-white text-[#313336] transition-all';

    const copyButtonClassName = classNames(buttonGenericStyles, {
        '!bg-[#116329] !text-white': copied,
    });

    const isPreviewMode = mode === 'preview' || !mode;

    const actionButtonsClassName = classNames(
        'absolute top-1.5 right-1.5 z-10 group-hover:opacity-100 flex items-center gap-2 transition-all',
        COPY_EXCLUDED_ELEMENT_CLASS_NAME,
        {
            'opacity-100': isLangSelectOpened && !isPreviewMode,
            'opacity-0': !isLangSelectOpened || isPreviewMode,
        }
    );

    const editorClassName = classNames('relative group', COPY_EXCLUDED_ELEMENT_CLASS_NAME, {
        hidden: isPreviewMode,
    });

    const containerClassName = classNames(
        CODE_BLOCK_CLASS_NAME,
        isPreviewMode && CODE_BLOCK_PREVIEW_MODE_CLASS_NAME
    );

    return (
        <div className={containerClassName}>
            <div className={editorClassName}>
                <div
                    className="_codeMirrorWrapper_uazmk_391 relative"
                    onKeyDown={(e) => e.stopPropagation()}
                >
                    <div ref={elRef} />
                </div>

                <div className={actionButtonsClassName}>
                    <CodeLanguageSelect
                        language={language}
                        disabled={readOnly}
                        onChange={onLanguageChange}
                        isLangSelectOpened={isLangSelectOpened}
                        toggleLangSelect={toggle}
                    />

                    {LANGUAGES_WITH_PREVIEW.includes(language) && (
                        <button className={buttonGenericStyles} onClick={() => setMode('preview')}>
                            <SvgIcon color="currentColor" icon={EyeIcon} size="medium" />
                        </button>
                    )}

                    <button
                        className={copyButtonClassName}
                        onClick={() => {
                            setCopied(true);
                            handleCopy(lexicalNode.__code);
                        }}
                    >
                        <SvgIcon
                            color="currentColor"
                            icon={copied ? CheckLineIcon : CopyIcon}
                            size="medium"
                        />
                    </button>
                </div>
            </div>

            <pre className="whitespace-pre-wrap hidden">
                <code className="whitespace-pre-wrap font-body text-body-regular-s text-neutral-500 hidden">
                    {lexicalNode.__code}
                </code>
            </pre>

            {isPreviewMode && (
                <PreviewMode code={code} language={language} openEditMode={() => setMode('edit')} />
            )}
        </div>
    );
};

export const CodeEditorDescriptor = {
    match: (language, meta) => true,
    priority: 0,
    Editor: CodeMirrorEditor,
};
